import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import isBetween from 'dayjs/plugin/isBetween'
import { AddToCalendarButton } from 'add-to-calendar-button-react'
import pLimit from 'p-limit'

import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Container,
  Dialog,
  IconButton,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import VideoIcon from '@mui/icons-material/Videocam'
import BackArrowIcon from '@mui/icons-material/ArrowBack'
import VerifiedIcon from '@mui/icons-material/Verified'

import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import Loading from 'src/v3/components/loading'
import { accreditationDict } from 'src/v3/events/accreditations'
import {
  selectRoles,
  selectUseEvents,
  selectUserID,
} from 'src/v3/settings/settings.slice'
import {
  fetchEvents,
  fetchAccreditations,
  fetchEventAccreditations,
  selectAccreditationsStatus,
  selectEventAccreditationsStatus,
  selectEventById,
  selectEventsStatus,
  selectAccreditationsByTypeByEvent,
  selectSessionsByEventID,
  selectSessionsStatus,
  fetchSessions,
  selectInvites,
  selectCompletionEventsByID,
  selectCompletionEventsStatusByID,
  fetchEventCompletionEvents,
  selectUsersByLinkedService,
  selectUsersStatusByLinkedService,
  fetchUsers,
  fetchEventCompletionEventsBySession,
  selectQuizByEvent,
  selectQuizStatusByEvent,
  fetchQuiz,
} from './events.slice'
import {
  CalendarIcon,
  ClockIcon,
  CloseIcon,
  ConfirmIcon,
  LocationIcon,
  UserIcon,
} from '../core/icons'
import { Event, EventSession } from '../core/types'

dayjs.extend(utc)
dayjs.extend(isBetween)

let SessionPanel = ({
  event,
  session,
  registered,
  index,
  attendanceVisible,
  setOpenSession,
}: {
  event?: Event
  session: EventSession
  registered: boolean
  index: number
  attendanceVisible: boolean
  setOpenSession: React.Dispatch<React.SetStateAction<string>>
}): ReactElement => (
  <Card sx={{ p: 2, bgcolor: 'background.paperDark' }}>
    <div style={{ position: 'relative' }}>
      <Chip
        variant="outlined"
        label={registered ? 'Registered' : 'Available'}
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          borderRadius: 1,
        }}
      />
    </div>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography color={registered ? 'primary' : 'textPrimary'} sx={{ mb: 1 }}>
        Session {index + 1}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <CalendarIcon sx={{ height: 18, width: 18 }} />
        <Typography color="textSecondary">
          {dayjs(session.sessionTime).format('LL')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <ClockIcon sx={{ height: 18, width: 18 }} />
        <Typography color="textSecondary">
          {dayjs(session.sessionTime).format('h:mm A')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <LocationIcon sx={{ height: 18, width: 18 }} />
        <Box>
          <Typography color="textSecondary">
            {event?.location.kind !== 'inPerson' && 'Virtual event access'}
          </Typography>
          <Typography color="textSecondary">
            {event?.location.kind !== 'virtual' &&
              `${event?.location.address}, ${event?.location.city}, ${event?.location.state} ${event?.location.zip}`}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <UserIcon sx={{ height: 18, width: 18 }} />
        <Typography color="textSecondary">{session.instructor}</Typography>
      </Box>
    </Box>
    {registered && (
      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        {event?.location.kind !== 'inPerson' &&
          event?.location.url &&
          dayjs(session.sessionTime).isBetween(
            dayjs(),
            dayjs().add(30, 'months'), // TODO: change to minutes
          ) && (
            <Button
              sx={{ textTransform: 'none' }}
              variant="outlined"
              startIcon={<VideoIcon />}
              href={event.location.url}
              target="_blank"
            >
              Join Virtual Session
            </Button>
          )}
        <Box sx={{ m: '-1px' }}>
          <AddToCalendarButton
            name={event?.name}
            options={['Apple', 'Google']}
            location={event?.location.name}
            startDate={dayjs(session.sessionTime).format('YYYY-MM-DD')}
            endDate={dayjs(session.sessionTime).format('YYYY-MM-DD')}
            startTime={dayjs(session.sessionTime).format('HH:mm')}
            endTime={dayjs(session.sessionTime)
              .add(event?.hours || 1, 'hours')
              .format('HH:mm')}
            // timeZone="UTC"
          ></AddToCalendarButton>
        </Box>
        {dayjs(session.sessionTime).isAfter(dayjs()) && attendanceVisible && (
          <Button // TODO: change ^^^ to isBefore
            sx={{ textTransform: 'none' }}
            variant="outlined"
            startIcon={<LocationIcon />}
            onClick={() => setOpenSession(session.id)}
          >
            Mark Attendance
          </Button>
        )}
      </Box>
    )}
    {!registered &&
      dayjs(session.sessionTime).isAfter(dayjs()) && // TODO: change to isBefore
      attendanceVisible && (
        <Button
          sx={{ mt: 1, textTransform: 'none' }}
          variant="outlined"
          startIcon={<LocationIcon />}
          onClick={() => setOpenSession(session.id)}
        >
          Mark Attendance
        </Button>
      )}
  </Card>
)

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { eventId } = useParams()

  let useEvents = useAppSelector(selectUseEvents)
  let roles = useAppSelector(selectRoles)
  let userID = useAppSelector(selectUserID)
  let isManager = roles.includes('manager')

  let eventID = eventId || ''
  let event = useAppSelector(selectEventById(eventID))
  let lsid = event?.linkedServiceID || ''
  let eventsStatus = useAppSelector(selectEventsStatus)
  let accreditations = useAppSelector(
    selectAccreditationsByTypeByEvent(eventID),
  )
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)
  let eventAccreditationsStatus = useAppSelector(
    selectEventAccreditationsStatus,
  )
  let sessions = useAppSelector(selectSessionsByEventID(eventID))
  let sessionsStatus = useAppSelector(selectSessionsStatus)
  let invites = useAppSelector(selectInvites)
  let users = useAppSelector(selectUsersByLinkedService(lsid))
  let usersStatus = useAppSelector(selectUsersStatusByLinkedService(lsid))
  let completionEvents = useAppSelector(selectCompletionEventsByID(eventID))
  let completionEventsStatus = useAppSelector(
    selectCompletionEventsStatusByID(eventID),
  )
  let quiz = useAppSelector(selectQuizByEvent(eventID))
  let quizStatus = useAppSelector(selectQuizStatusByEvent(eventID))

  let [openSession, setOpenSession] = useState<string>('')
  let [passcode, setPasscode] = useState<string>('')
  let [passcodeError, setPasscodeError] = useState<boolean>(false)
  let [attendees, setAttendees] = useState<string[]>([])
  let [loading, setLoading] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let sessionCompletionEvents = useAppSelector(
    selectCompletionEventsByID(openSession),
  )
  let sessionCompletionEventsStatus = useAppSelector(
    selectCompletionEventsStatusByID(openSession),
  )

  let registeredSessions = sessions.filter((s) =>
    invites[s.id]?.some((i) => i.userID === userID),
  )
  let availableSessions = sessions.filter(
    (s) => !invites[s.id]?.some((i) => i.userID === userID),
  )

  let recentSession = registeredSessions
    // .filter((s) => dayjs(s.sessionTime).isBefore(dayjs())) // TODO: enable this filter
    .sort((a, b) => dayjs(b.sessionTime).diff(a.sessionTime, 'hours'))[0]
  let eventCompleted = completionEvents?.some(
    (ce) => ce.sessionID === recentSession?.id,
  )

  let sessionUsers =
    users
      ?.filter(
        (u) =>
          invites[openSession]?.some((inv) => inv.userID === u.id) &&
          !sessionCompletionEvents?.some((ce) => ce.userID === u.id),
      )
      .sort((a, b) => a.firstName.localeCompare(b.firstName)) || []

  let attendanceVisible =
    (event?.attendanceTrackingMechanism.kind === 'quizPasscode' &&
      registeredSessions.length &&
      !eventCompleted) ||
    (event?.attendanceTrackingMechanism.kind === 'managerTracked' && isManager)

  // eslint-disable-next-line fp/no-mutating-methods
  let accreditationNames = [
    ...Object.entries(accreditations).reduce(
      (all, [name, data]) =>
        data.length && name !== 'ceBroker'
          ? [...all, accreditationDict[name as 'virginia']]
          : all,
      [] as string[],
    ),
    ...accreditations.ceBroker.map(
      (a) => `CE Broker: ${a.state[0].toUpperCase()}${a.state.slice(1)}`,
    ),
  ].sort()

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus])

  useEffect(() => {
    if (accreditationsStatus === 'uninitialized') {
      dispatch(fetchAccreditations())
    }
  }, [accreditationsStatus])

  useEffect(() => {
    if (eventAccreditationsStatus === 'uninitialized') {
      dispatch(fetchEventAccreditations())
    }
  }, [eventAccreditationsStatus])

  useEffect(() => {
    if (sessionsStatus === 'uninitialized') {
      dispatch(fetchSessions())
    }
  }, [sessionsStatus])

  useEffect(() => {
    if (lsid && (!usersStatus || usersStatus === 'uninitialized')) {
      dispatch(fetchUsers(lsid))
    }
  }, [usersStatus])

  useEffect(() => {
    if (
      eventId &&
      (!completionEventsStatus || completionEventsStatus === 'uninitialized')
    ) {
      dispatch(fetchEventCompletionEvents({ eventID: eventId, userID }))
    }
  }, [completionEventsStatus, eventId])

  useEffect(() => {
    if (
      openSession &&
      (!sessionCompletionEventsStatus ||
        sessionCompletionEventsStatus === 'uninitialized')
    ) {
      dispatch(fetchEventCompletionEventsBySession(openSession))
    }
  }, [sessionCompletionEventsStatus, openSession])

  useEffect(() => {
    if ((eventID && !quizStatus) || quizStatus === 'uninitialized') {
      dispatch(fetchQuiz(eventID))
    }
  }, [quizStatus, eventID])

  return useEvents ? (
    <Container maxWidth="xl">
      <Box sx={{ mt: -18 }}></Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/events')}>
          <BackArrowIcon />
        </IconButton>
        <Typography variant="h5">{event?.name}</Typography>
      </Box>
      <img
        style={{ width: '100%', aspectRatio: '4 / 1', borderRadius: '10px' }}
        src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${event?.banner?.replace('originals', 'resized')}`}
      />
      <Box
        sx={{
          mt: 4,
          display: 'grid',
          gridTemplateColumns: {
            sm: '1fr',
            md: '5fr 2fr',
          },
          gap: 2.5,
        }}
      >
        <Box>
          <Typography variant="h6">About Event</Typography>
          <Card sx={{ p: 2, mt: 1, bgcolor: 'background.paperDark' }}>
            <Typography color="textSecondary">{event?.description}</Typography>
          </Card>
        </Box>
        <Box sx={{ height: { sm: 'fit-content', md: 0 } }}>
          <Typography variant="h6">Accreditations</Typography>
          <Card sx={{ p: 2, mt: 1, bgcolor: 'background.paperDark' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {!accreditationNames.length ? (
                <Typography color="textSecondary">
                  This event has no accreditations.
                </Typography>
              ) : (
                accreditationNames.map((a) => (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <VerifiedIcon color="primary" />
                    <Typography>{a}</Typography>
                  </Box>
                ))
              )}
            </Box>
          </Card>
          <Typography sx={{ mt: 2 }} variant="h6">
            Event Quiz
          </Typography>
          <Card sx={{ p: 2, mt: 1, bgcolor: 'background.paperDark' }}>
            {!quiz ? (
              <Typography color="textSecondary">
                This event does not have a quiz. Your attendance will be
                recorded by a manager.
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => navigate(`/events/${event?.id}/quiz`)}
                  disabled={!eventCompleted}
                >
                  Start Quiz
                </Button>
                {!eventCompleted && (
                  <Typography color="textSecondary">
                    The quiz cannot be taken until{' '}
                    {event?.attendanceTrackingMechanism.kind === 'quizPasscode'
                      ? "you enter your session's quiz passcode."
                      : 'your manager unlocks it by marking your attendance.'}
                  </Typography>
                )}
              </Box>
            )}
          </Card>
        </Box>
        <Box>
          <Typography variant="h6">Registered Sessions</Typography>
          <Box
            sx={{
              mt: 1,
              mb: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {!registeredSessions.length ? (
              <Card sx={{ p: 2, bgcolor: 'background.paperDark' }}>
                You have not registered for any event sessions.
              </Card>
            ) : (
              registeredSessions.map((s, i) => (
                <SessionPanel
                  key={s.id}
                  event={event}
                  session={s}
                  registered={true}
                  index={i}
                  attendanceVisible={attendanceVisible}
                  setOpenSession={setOpenSession}
                />
              ))
            )}
          </Box>
          <Typography variant="h6">Available Sessions</Typography>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {!availableSessions.length ? (
              <Card sx={{ p: 2, bgcolor: 'background.paperDark' }}>
                There are no available event sessions at this time.
              </Card>
            ) : (
              availableSessions.map((s, i) => (
                <SessionPanel
                  key={s.id}
                  event={event}
                  session={s}
                  registered={false}
                  index={i}
                  attendanceVisible={attendanceVisible}
                  setOpenSession={setOpenSession}
                />
              ))
            )}
          </Box>
        </Box>
      </Box>
      <Loading
        entities={[
          accreditationsStatus,
          eventAccreditationsStatus,
          eventsStatus,
          completionEventsStatus,
        ]}
      />
      <Dialog open={!!openSession} onClose={() => setOpenSession('')}>
        <Box sx={{ p: 2, minWidth: 280, maxWidth: 640 }}>
          <Typography variant="h6" textAlign="center" sx={{ mb: 1 }}>
            Confirm Attendance
          </Typography>
          {event?.attendanceTrackingMechanism.kind === 'quizPasscode' ? (
            <TextField
              fullWidth
              variant="outlined"
              label="Quiz Passcode"
              error={passcodeError}
              helperText={passcodeError ? 'Incorrect quiz passcode' : undefined}
              value={passcode}
              onChange={(e) => {
                setPasscodeError(false)
                setPasscode(e.target.value)
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {sessionUsers.map((u) => (
                <Chip
                  label={`${u.firstName} ${u.lastName}`}
                  icon={
                    attendees.includes(u.id) ? <ConfirmIcon /> : <CloseIcon />
                  }
                  color={attendees.includes(u.id) ? 'success' : 'default'}
                  clickable
                  onClick={() =>
                    setAttendees(
                      attendees.includes(u.id)
                        ? attendees.filter((cu) => cu !== u.id)
                        : [...attendees, u.id],
                    )
                  }
                />
              ))}
            </Box>
          )}
          <Box
            sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1 }}
          >
            <Button
              variant="contained"
              loading={loading}
              onClick={() => {
                if (
                  event?.attendanceTrackingMechanism.kind === 'quizPasscode' &&
                  event?.attendanceTrackingMechanism.quizPasscode !== passcode
                ) {
                  setPasscodeError(true)
                  return
                }
                setLoading(true)
                let request =
                  event?.attendanceTrackingMechanism.kind === 'quizPasscode'
                    ? axios.post('/api/event/completion-event', {
                        userID,
                        sessionID: openSession,
                      })
                    : axios
                        .post('/api/event/completion-events', {
                          userIDs: attendees,
                          sessionID: openSession,
                        })
                        .then((): Promise<any> => {
                          if (event?.hours && !quiz) {
                            let limit = pLimit(6)

                            let uploadPromises = attendees.map((attendee) =>
                              limit(() =>
                                axios.post('/api/event/ce-record', {
                                  eventID,
                                  userID: attendee,
                                }),
                              ),
                            )

                            return Promise.all(uploadPromises)
                          } else {
                            return Promise.resolve()
                          }
                        })
                request
                  .then(() => {
                    dispatch(
                      fetchEventCompletionEvents({
                        eventID: eventId || '',
                        userID,
                      }),
                    )
                    dispatch(fetchEventCompletionEventsBySession(openSession))
                  })
                  .catch((err) =>
                    setErrorMessage(
                      err?.message ||
                        err?.data?.message ||
                        'Error tracking attendance',
                    ),
                  )
                  .finally(() => {
                    setLoading(false)
                    setAttendees([])
                    setOpenSession('')
                  })
              }}
            >
              Confirm
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                setAttendees([])
                setOpenSession('')
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  ) : (
    <></>
  )
}
